import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { runsApi } from "../api/endpoints";
import { SkeletonTable } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { SeverityChip } from "../components/chips/SeverityChip";
import { FactChip } from "../components/chips/FactChip";
import { SEVERITY_ORDER } from "../lib/constants";
import { formatDateTime } from "../lib/format";

/**
 * Findings inbox — cross-run view of engine findings for the workspace's
 * latest completed run. Filters mirror the backend contract exactly.
 */
export default function FindingsInboxPage() {
  const { workspaceId } = useParams();
  const [params, setParams] = useSearchParams();
  const [runs, setRuns] = useState(null);
  const [findings, setFindings] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(params.get("run") || "");
  const [severity, setSeverity] = useState(params.get("severity") || "");
  const [ruleId, setRuleId] = useState(params.get("rule_id") || "");
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setError(null);
    runsApi
      .list(workspaceId)
      .then((r) => {
        if (!mounted) return;
        const completed = (r || []).filter((x) => x.status === "completed");
        setRuns(completed);
        if (!selectedRunId && completed[0]) {
          setSelectedRunId(completed[0].id);
        }
      })
      .catch((e) => mounted && setError(e));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (!selectedRunId) return;
    let mounted = true;
    setFindings(null);
    setError(null);
    const filters = { page_size: 500 };
    if (severity) filters.severity = severity;
    if (ruleId) filters.rule_id = ruleId;
    runsApi
      .findings(workspaceId, selectedRunId, filters)
      .then((f) => mounted && setFindings(f))
      .catch((e) => mounted && setError(e));
    return () => {
      mounted = false;
    };
  }, [workspaceId, selectedRunId, severity, ruleId]);

  const filtered = useMemo(() => {
    const items = findings?.items || [];
    if (!q) return items;
    const needle = q.toLowerCase();
    return items.filter(
      (f) =>
        (f.rule_id || "").toLowerCase().includes(needle) ||
        (f.calculation || "").toLowerCase().includes(needle) ||
        (f.process_id || "").toLowerCase().includes(needle),
    );
  }, [findings, q]);

  function updateParams(next) {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v === "" || v === undefined || v === null) p.delete(k);
      else p.set(k, v);
    });
    setParams(p, { replace: true });
  }

  return (
    <div className="space-y-5">
      <header>
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Findings
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">
          Findings inbox
        </h1>
      </header>

      <div className="rounded border border-slate-200 bg-white p-4 flex flex-wrap gap-3 items-end">
        <label className="text-xs text-slate-600">
          <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
            Run
          </span>
          <select
            data-testid="inbox-run-select"
            value={selectedRunId}
            onChange={(e) => {
              setSelectedRunId(e.target.value);
              updateParams({ run: e.target.value });
            }}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm min-w-[16rem]"
          >
            {runs === null && <option value="">Loading…</option>}
            {runs !== null && runs.length === 0 && (
              <option value="">No completed runs</option>
            )}
            {runs?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} · {formatDateTime(r.created_at)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
            Severity
          </span>
          <select
            data-testid="inbox-severity"
            value={severity}
            onChange={(e) => {
              setSeverity(e.target.value);
              updateParams({ severity: e.target.value });
            }}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {SEVERITY_ORDER.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
            Rule id
          </span>
          <input
            data-testid="inbox-rule-id"
            value={ruleId}
            onChange={(e) => {
              setRuleId(e.target.value);
              updateParams({ rule_id: e.target.value });
            }}
            placeholder="e.g. BQ-001"
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm w-36"
          />
        </label>
        <label className="text-xs text-slate-600 relative">
          <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
            Search
          </span>
          <Search
            size={12}
            className="absolute left-2 bottom-2 text-slate-400"
          />
          <input
            data-testid="inbox-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="calculation, process id…"
            className="rounded border border-slate-300 bg-white pl-6 pr-2 py-1 text-sm w-56"
          />
        </label>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      ) : findings === null ? (
        <SkeletonTable rows={6} cols={5} />
      ) : filtered.length === 0 ? (
        <EmptyState title="No findings match those filters." />
      ) : (
        <div className="rounded border border-slate-200 bg-white overflow-hidden">
          <table
            className="w-full text-sm"
            data-testid="inbox-table"
          >
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Severity</th>
                <th className="px-4 py-2.5">Rule</th>
                <th className="px-4 py-2.5">Process</th>
                <th className="px-4 py-2.5">Calculation</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((f) => (
                <tr
                  key={f.id}
                  data-testid={`inbox-row-${f.id}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <SeverityChip severity={f.effective_severity} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FactChip source="FINDING" />
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
                        {f.rule_id} · v{f.rule_version}
                      </code>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {f.process_id ? (
                      <Link
                        to={`/w/${workspaceId}/analyses/${selectedRunId}/processes/${f.process_id}`}
                        className="text-sm font-medium text-slate-900 hover:underline"
                      >
                        {f.process_id}
                      </Link>
                    ) : (
                      <span className="text-slate-500 italic text-xs">
                        library-scope
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-800 font-mono text-xs">
                    {f.calculation}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {f.process_id && (
                      <Link
                        to={`/w/${workspaceId}/analyses/${selectedRunId}/processes/${f.process_id}`}
                        className="text-xs text-slate-500 hover:text-slate-900 underline"
                      >
                        Open
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
